import { ThemeEvents } from '@theme/events';

/**
 * Keeps the product-label overlay in sync with the selected variant.
 *
 * PORT NOTE (2026-08-28). The v2.0.0 original listens for
 * StandardEvents.productSelect from '@shopify/events' and awaits an event
 * promise. v1.1.3 has no '@shopify/events' entry in its importmap at all, so
 * that version cannot run here — importing it would throw on module resolution
 * and take the element with it.
 *
 * This rewrite mirrors assets/product-price.js instead, which is v1.1.3's own
 * variant-sync component: listen for ThemeEvents.variantUpdate on the closest
 * section, and read the already-rendered markup off event.detail.data.html.
 * No promise to await — v1.1.3 hands the parsed document over synchronously.
 *
 * All label logic stays server-side in snippets/bmp-product-labels.liquid, so
 * there is still exactly one source of truth. This file only swaps markup.
 */
class BmpProductLabels extends HTMLElement {
  connectedCallback() {
    const scope = this.closest('.shopify-section, dialog');
    if (!scope) return;
    scope.addEventListener(ThemeEvents.variantUpdate, this.#update);
  }

  disconnectedCallback() {
    const scope = this.closest('.shopify-section, dialog');
    if (!scope) return;
    scope.removeEventListener(ThemeEvents.variantUpdate, this.#update);
  }

  #update = (event) => {
    // Same ownership guard product-price.js:38-42 uses. A card and the product
    // page can share a section, so without this a swatch change on one card
    // would repaint every overlay in that section.
    if (event.detail?.data?.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const html = event.detail?.data?.html;
    if (!html) return;

    // Match on context so a card never adopts the product page's inset scale
    // (the fetched document is the PDP, whose element carries --pdp).
    const context = this.dataset.context;
    const next =
      html.querySelector(`bmp-product-labels[data-context="${context}"]`) ??
      html.querySelector('bmp-product-labels');
    if (!next) return;

    // Children only — this element's own class/style/context attributes stay put.
    if (this.innerHTML !== next.innerHTML) this.innerHTML = next.innerHTML;
  };
}

if (!customElements.get('bmp-product-labels')) {
  customElements.define('bmp-product-labels', BmpProductLabels);
}
